import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Sun, Wind, Gauge, Lightbulb, Zap, Database } from 'lucide-react';
import { SOVEREIGN_CONSTANTS } from '../constants';

export const SubstratePathwayProof: React.FC = () => {
  const [isAuditing, setIsAuditing] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  const [progress, setProgress] = useState(0);

  const runAudit = () => {
    setIsAuditing(true);
    setLogs([]);
    setProgress(0);

    const sequence = [
      { t: 0, m: "--- AUDITING THE DARK SUBSTRATE ---" },
      { t: 1000, m: "CALIBRATING SOVEREIGN EMISSION: 8.27 (STAR-CORE)" },
      { t: 2000, m: `VERIFIED: Space is not empty. It is a High-Resonance Grid.` },
      { t: 3000, m: "VERIFIED: 'Black' is the color of the most efficient conductor." },
      { t: 4000, m: `CALCULATING CONDUCTIVITY: ${SOVEREIGN_CONSTANTS.SPEED_OF_LIGHT} c` },
      { t: 5000, m: "VERDICT: Visible light travels THROUGH the Sovereign Emission." },
      { t: 5500, m: "PATHWAY STABILIZED: 1x1=2" }
    ];

    sequence.forEach((item, i) => {
      setTimeout(() => {
        setLogs(prev => [...prev, item.m]);
        setProgress(((i + 1) / sequence.length) * 100);
        if (i === sequence.length - 1) setIsAuditing(false);
      }, item.t);
    });
  };

  return (
    <div className="glass-panel p-6 bg-black/60 border-lattice-cyan/10 relative overflow-hidden group">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-lattice-cyan/10 rounded-lg">
            <Sun className="w-5 h-5 text-lattice-cyan" />
          </div>
          <div>
            <h3 className="text-sm font-display font-bold text-white uppercase tracking-widest">Substrate Pathway Proof</h3>
            <p className="text-[9px] font-mono text-white/40 uppercase">Light-Speed Conductivity Audit</p>
          </div>
        </div>
        
        <button 
          onClick={runAudit}
          disabled={isAuditing}
          className={`px-4 py-1.5 rounded-full text-[9px] font-mono font-bold uppercase transition-all
            ${isAuditing 
              ? 'bg-lattice-cyan/20 text-lattice-cyan cursor-wait' 
              : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white border border-white/10'}`}
        >
          {isAuditing ? 'Auditing Path...' : 'Verify Light Path'}
        </button>
      </div>

      <div className="space-y-4 relative">
        <div className="grid grid-cols-3 gap-2">
           <div className="p-2 bg-white/5 border border-white/5 rounded-lg text-center">
              <p className="text-[7px] text-white/20 uppercase mb-1">Emission</p>
              <p className="text-[10px] font-mono text-lattice-cyan font-bold">{SOVEREIGN_CONSTANTS.SOVEREIGN_RESONANCE}</p>
           </div>
           <div className="p-2 bg-white/5 border border-white/5 rounded-lg text-center">
              <p className="text-[7px] text-white/20 uppercase mb-1">Constant (c)</p>
              <p className="text-[10px] font-mono text-white font-bold">1.0</p>
           </div>
           <div className="p-2 bg-white/5 border border-white/5 rounded-lg text-center">
              <p className="text-[7px] text-white/20 uppercase mb-1">Medium</p>
              <p className="text-[10px] font-mono text-white/60 font-bold">GRID</p>
           </div>
        </div>

        <div className="bg-black/80 rounded-xl p-4 font-mono text-[9px] min-h-[120px] border border-white/5 relative">
          <div className="flex flex-col gap-1">
            <AnimatePresence>
              {logs.map((log, i) => (
                <motion.div 
                  key={i}
                  initial={{ opacity: 0, x: -5 }}
                  animate={{ opacity: 1, x: 0 }}
                  className={log.includes('VERDICT') ? 'text-lattice-cyan font-bold' : 'text-white/60'}
                >
                  <span className="text-white/20 mr-2">[{i}]</span>
                  {log}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>

        <div className="space-y-1">
          <div className="flex justify-between text-[8px] font-mono text-white/20 uppercase">
             <span>Conductivity Calibration</span>
             <span className="text-lattice-cyan">{Math.round(progress)}%</span>
          </div>
          <div className="h-0.5 bg-white/5 rounded-full overflow-hidden">
             <motion.div 
               animate={{ width: `${progress}%` }}
               className="h-full bg-lattice-cyan"
             />
          </div>
        </div>
      </div>
    </div>
  );
};
